import { CarouselDots, CircleIcon, PrimaryButton, ReactIcons } from "@kalyzee/kalyzee-ui";

import { useElementSize, useRefAutoUpdate } from "@kalyzee/kast-web-sdk/web-components";
import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import clsx from "clsx";
import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { isHorizontalStripLayout, isVerticalStripLayout, LayoutType } from "../../../interfaces/ui";
import { getDefaultPlayerGridItemRendererProvider } from "../PlayersGrid";
import { PlayerItem, PlayerRendererProvider, PlayerRendererProviderOptions } from "../interfaces";
import styles from "./NewTriLayoutContainer.module.css";

/** Directional focus icon — exported so consumers can build a focus action button. */
export const focusIconForLayout = (layout: LayoutType) => {
  switch (layout) {
    case LayoutType.Top:
      return ReactIcons.SelectMainDown;
    case LayoutType.Bottom:
      return ReactIcons.SelectMainVertical;
    case LayoutType.Left:
      return ReactIcons.SelectMainRight;
    case LayoutType.Right:
      return ReactIcons.SelectMainHorizontal;
    default:
      return ReactIcons.Expand;
  }
};

/* -------------------------------------------------------------------------- */
/*                                 Constants                                  */
/* -------------------------------------------------------------------------- */

const DEFAULT_THUMB_RATIO = 16 / 9;
const DEFAULT_THUMB_SHORT = 135;
const STRIP_PADDING = 16;
const GRID_GAP = 12;
const TRANSITION_MS = 320;

/** Hardcoded grid templates 1..9 (mirrors MultipleVideoPlayer's default layout). */
const GRID_TEMPLATES: string[][][] = [
  [["a"]],
  [["a", "b"]],
  [["a", "b"], ["c", "c"]],
  [["a", "b"], ["c", "d"]],
  [["a", "a", "a", "b", "b", "b"], ["c", "c", "d", "d", "e", "e"]],
  [["a", "b", "c"], ["d", "e", "f"]],
  [["a", "a", "a", "b", "b", "b"], ["c", "c", "c", "d", "d", "d"], ["e", "e", "f", "f", "g", "g"]],
  [["a", "a", "a", "b", "b", "b"], ["c", "c", "d", "d", "e", "e"], ["f", "f", "g", "g", "h", "h"]],
  [["a", "a", "b", "b", "c", "c"], ["d", "d", "e", "e", "f", "f"], ["g", "g", "h", "h", "i", "i"]],
];
const GRID_PAGE_SIZE = GRID_TEMPLATES.length;
const TARGET_RATIO = 16 / 9;
const LETTERS = "abcdefghijklmnopqrstuvwxyz";

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

export interface NewTriLayoutContainerItemVisibility {
  item: PlayerItem;
  visible: boolean;
}

export interface NewTriLayoutContainerRef {
  getVisibility: () => NewTriLayoutContainerItemVisibility[];
}

export interface NewTriLayoutContainerRenderItemContext {
  item: PlayerItem;
  isFocused: boolean;
  isInStrip: boolean;
  isFullscreen: boolean;
  /** Move this item into the focused slot. Meaningful only in split layouts. */
  onFocus: () => void;
}

export interface NewTriLayoutContainerProps {
  meeting?: Meeting;
  sessions?: MeetingSession[];
  items: PlayerItem[];
  layout: LayoutType;

  /** Controlled focused item (the "main" in split layouts). */
  focusedItemId?: string;
  defaultFocusedItemId?: string;
  onFocusChange?: (id: string) => void;

  /**
   * When set, only this item is rendered, full-bleed (no padding, no border
   * radius). Other items are unmounted. Use this together with a parent
   * `<FullscreenContainer>` to push the trilayout into browser fullscreen.
   */
  fullscreenItemId?: string;

  /** Whether to display page navigation when the strip/grid overflows. Default: true. */
  paging?: boolean;

  /** Aspect ratio of strip thumbnails (width / height). Default: 16/9. */
  thumbRatio?: number;
  /** Short side of the thumbnail in pixels. Default: 135. */
  thumbShortSide?: number;

  style?: React.CSSProperties;
  className?: string;

  defaultRendererProviderOptions?: PlayerRendererProviderOptions;
  videosVisibilityUpdated?: (visibility: NewTriLayoutContainerItemVisibility[]) => void;

  /** Render the item content. The container provides `controls` JSX in `ctx`. */
  renderItem?: (
    rendererProvider: PlayerRendererProvider,
    ctx: NewTriLayoutContainerRenderItemContext,
  ) => React.ReactNode;
}

interface ItemPlacement {
  gridArea?: string;
  hidden: boolean;
  zIndex: number;
}

interface LayoutComputation {
  containerStyle: React.CSSProperties;
  placements: Map<string, ItemPlacement>;
  visibility: Map<string, boolean>;
  totalStripPages: number;
  totalGridPages: number;
}

/* -------------------------------------------------------------------------- */
/*                              Layout calculation                            */
/* -------------------------------------------------------------------------- */

const pickFullTemplate = (count: number, width: number, height: number): string[][] | undefined => {
  if (count <= 0) return undefined;
  const tpl = GRID_TEMPLATES[Math.min(count, GRID_PAGE_SIZE) - 1];
  if (!tpl) return undefined;
  if (count <= 2) return tpl;
  if (width <= 0 || height <= 0) return tpl;
  const rows = tpl.length;
  const cols = tpl[0].length;
  const elementRatio = width / height;
  const ratio = (elementRatio * rows) / cols;
  const ratioInverted = (elementRatio * cols) / rows;
  if (Math.abs(TARGET_RATIO - ratioInverted) < Math.abs(TARGET_RATIO - ratio)) {
    const inverted: string[][] = [];
    tpl.forEach((line, rowIdx) => {
      line.forEach((v, colIdx) => {
        if (!inverted[colIdx]) inverted[colIdx] = [];
        inverted[colIdx][rowIdx] = v;
      });
    });
    return inverted;
  }
  return tpl;
};

const computeLayout = (
  items: PlayerItem[],
  layout: LayoutType,
  containerSize: { width: number; height: number },
  focusedItemId: string | undefined,
  fullscreenItemId: string | undefined,
  stripPage: number,
  gridPage: number,
  thumbWidth: number,
  thumbHeight: number,
): LayoutComputation => {
  const placements = new Map<string, ItemPlacement>();
  const visibility = new Map<string, boolean>();

  const w = containerSize.width || 0;
  const h = containerSize.height || 0;

  /* -------------------- Fullscreen mode -------------------- */
  if (fullscreenItemId) {
    // If the explicitly fullscreen item disappears (session left, page unload,
    // etc.), silently fall back to items[0] instead of showing a black hole.
    // The consumer's state stays as-is — when the original item comes back
    // we'll re-target it naturally on the next render.
    const fullscreenItem = items.find((i) => i.getId() === fullscreenItemId) ?? items[0];
    if (fullscreenItem) {
      const fullscreenId = fullscreenItem.getId();
      // Stack everyone in the single cell. The fullscreen item on top with
      // visibility=true; others rendered hidden (opacity:0) so they stay
      // mounted — same principle as the rest of the trilayout: never unmount
      // a mounted player just because another took the spotlight.
      items.forEach((item) => {
        const id = item.getId();
        if (id === fullscreenId) {
          placements.set(id, { gridArea: "1 / 1 / -1 / -1", hidden: false, zIndex: 1 });
          visibility.set(id, true);
        } else {
          placements.set(id, { gridArea: "1 / 1 / -1 / -1", hidden: true, zIndex: 0 });
          visibility.set(id, false);
        }
      });
    } else {
      items.forEach((item) => visibility.set(item.getId(), false));
    }
    return {
      containerStyle: {
        gridTemplateRows: "minmax(0, 1fr)",
        gridTemplateColumns: "minmax(0, 1fr)",
      },
      placements,
      visibility,
      totalStripPages: 0,
      totalGridPages: 0,
    };
  }

  /* -------------------- Full mode -------------------- */

  if (layout === LayoutType.Full) {
    const itemsPerPage = GRID_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(items.length / itemsPerPage));
    const safePage = Math.min(gridPage, totalPages - 1);
    const start = safePage * itemsPerPage;
    const end = Math.min(start + itemsPerPage, items.length);
    const pageItems = items.slice(start, end);
    const template = pickFullTemplate(pageItems.length, w, h);

    if (template) {
      const cssAreas = template.map((row) => `"${row.join(" ")}"`).join(" ");
      const rows = template.length;
      const cols = template[0].length;

      items.forEach((item, idx) => {
        const id = item.getId();
        if (idx < start || idx >= end) {
          // Off-page items are not added to the placements map at all — they
          // simply won't be rendered. The visibility callback still reports
          // them as `false` so WebRTC subscriptions pause.
          visibility.set(id, false);
          return;
        }
        const localIdx = idx - start;
        placements.set(id, { gridArea: LETTERS[localIdx], hidden: false, zIndex: 1 });
        visibility.set(id, true);
      });

      const containerStyle: React.CSSProperties = {
        gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
        gridTemplateAreas: cssAreas,
        gap: `${GRID_GAP}px`,
        padding: `${GRID_GAP}px`,
      };

      return {
        containerStyle,
        placements,
        visibility,
        totalStripPages: 0,
        totalGridPages: totalPages,
      };
    }
    return {
      containerStyle: {},
      placements,
      visibility,
      totalStripPages: 0,
      totalGridPages: 1,
    };
  }

  /* -------------------- Split mode (Top/Bottom/Left/Right) -------------------- */

  const focusedItem = items.find((i) => i.getId() === focusedItemId) ?? items[0];
  const stripItems = items.filter((i) => i !== focusedItem);

  const horizontal = isHorizontalStripLayout(layout);
  const vertical = isVerticalStripLayout(layout);

  const stripThickness = horizontal
    ? thumbHeight + 2 * STRIP_PADDING
    : thumbWidth + 2 * STRIP_PADDING;

  const usableStripLength = horizontal
    ? Math.max(0, w - 2 * STRIP_PADDING)
    : Math.max(0, h - 2 * STRIP_PADDING);

  const slotSize = horizontal ? thumbWidth + GRID_GAP : thumbHeight + GRID_GAP;
  const slotsPerPage = Math.max(1, Math.floor((usableStripLength + GRID_GAP) / slotSize));
  const totalStripPages = Math.max(1, Math.ceil(stripItems.length / slotsPerPage));
  const safeStripPage = Math.min(stripPage, totalStripPages - 1);
  const visibleStripStart = safeStripPage * slotsPerPage;
  const visibleStripEnd = Math.min(visibleStripStart + slotsPerPage, stripItems.length);
  const visibleStripCount = visibleStripEnd - visibleStripStart;

  // Use the *visible* count, not the page-size, so the 1fr fillers always
  // center the visible thumbs perfectly. With page-size we'd off-center by half
  // a slot whenever (slotsPerPage - visibleStripCount) was odd.
  const stripCellCount = Math.max(1, visibleStripCount);

  let containerStyle: React.CSSProperties;
  const focusedItemPlacement: ItemPlacement = { hidden: false, zIndex: 1 };

  // No strip items at all → degenerate to a single-cell grid so the focused
  // player fills 100% of the trilayout. Otherwise we'd reserve `stripThickness`
  // for the (empty) strip row/column.
  const noStrip = stripItems.length === 0;

  if (noStrip) {
    containerStyle = {
      gridTemplateRows: "minmax(0, 1fr)",
      gridTemplateColumns: "minmax(0, 1fr)",
      gap: `${GRID_GAP}px`,
      padding: `${GRID_GAP}px`,
    };
    focusedItemPlacement.gridArea = "1 / 1 / -1 / -1";
  } else if (horizontal) {
    const stripIsTop = layout === LayoutType.Top;
    const rows = stripIsTop
      ? `${stripThickness}px minmax(0, 1fr)`
      : `minmax(0, 1fr) ${stripThickness}px`;
    const slotCols = Array.from({ length: stripCellCount }, () => `${thumbWidth}px`).join(" ");
    const cols = `minmax(0, 1fr) ${slotCols} minmax(0, 1fr)`;
    containerStyle = {
      gridTemplateRows: rows,
      gridTemplateColumns: cols,
      gap: `${GRID_GAP}px`,
      padding: `${GRID_GAP}px`,
    };
    const focusedRow = stripIsTop ? 2 : 1;
    focusedItemPlacement.gridArea = `${focusedRow} / 1 / ${focusedRow + 1} / -1`;
  } else if (vertical) {
    const stripIsLeft = layout === LayoutType.Left;
    const cols = stripIsLeft
      ? `${stripThickness}px minmax(0, 1fr)`
      : `minmax(0, 1fr) ${stripThickness}px`;
    const slotRows = Array.from({ length: stripCellCount }, () => `${thumbHeight}px`).join(" ");
    const rows = `minmax(0, 1fr) ${slotRows} minmax(0, 1fr)`;
    containerStyle = {
      gridTemplateRows: rows,
      gridTemplateColumns: cols,
      gap: `${GRID_GAP}px`,
      padding: `${GRID_GAP}px`,
    };
    const focusedCol = stripIsLeft ? 2 : 1;
    focusedItemPlacement.gridArea = `1 / ${focusedCol} / -1 / ${focusedCol + 1}`;
  } else {
    containerStyle = {};
  }

  if (focusedItem) {
    placements.set(focusedItem.getId(), focusedItemPlacement);
    visibility.set(focusedItem.getId(), true);
  }

  stripItems.forEach((item, idx) => {
    const id = item.getId();
    const onCurrentPage = idx >= visibleStripStart && idx < visibleStripEnd;
    if (!onCurrentPage) {
      // Off-page items are not rendered (no entry in placements). Visibility
      // is still reported so WebRTC subscriptions pause.
      visibility.set(id, false);
      return;
    }
    const slotIdxOnPage = idx - visibleStripStart;
    if (horizontal) {
      const stripIsTop = layout === LayoutType.Top;
      const stripRow = stripIsTop ? 1 : 2;
      const col = 2 + slotIdxOnPage;
      placements.set(id, { gridArea: `${stripRow} / ${col} / ${stripRow + 1} / ${col + 1}`, hidden: false, zIndex: 1 });
    } else {
      const stripIsLeft = layout === LayoutType.Left;
      const stripCol = stripIsLeft ? 1 : 2;
      const row = 2 + slotIdxOnPage;
      placements.set(id, { gridArea: `${row} / ${stripCol} / ${row + 1} / ${stripCol + 1}`, hidden: false, zIndex: 1 });
    }
    visibility.set(id, true);
  });

  return {
    containerStyle,
    placements,
    visibility,
    totalStripPages,
    totalGridPages: 0,
  };
};

/* -------------------------------------------------------------------------- */
/*                          FLIP animation (discrete)                         */
/* -------------------------------------------------------------------------- */

interface PageChangeDirection {
  axis: "x" | "y";
  sign: 1 | -1;
}

/**
 * FLIP (First, Last, Invert, Play) animation — the same player <div> is never
 * remounted between layouts; we just make it *look* like it's still in its old
 * position, then animate back to identity:
 *   1. First  — `prevRectsRef` holds each item's getBoundingClientRect() from
 *               the previous commit (its old on-screen position/size).
 *   2. Last   — React re-renders with new gridArea / template; the browser
 *               lays the same <div> out at its new position. We measure
 *               `newRect` in this useLayoutEffect (synchronous, pre-paint).
 *   3. Invert — translate(dx, dy) scale(sx, sy) so the element visually sits
 *               exactly where it used to be, even though grid-wise it's
 *               already in the new cell. Force reflow via `offsetHeight`.
 *   4. Play   — next rAF, enable the transition and clear the transform.
 *               The browser interpolates from the inverted transform back to
 *               identity → smooth slide + scale.
 *
 * Only animates discrete state changes (layout/focus/page/select) — resize-
 * driven re-renders refresh the rect snapshot without animating, so dragging
 * the window or zooming doesn't spam transition starts.
 *
 * Behavior summary:
 *  - Visible → visible items: standard FLIP (translate + scale invert,
 *    animate to identity).
 *  - Newly mounted items (off-page → on-page) when `pageChange` is set:
 *    slide-in entrance — start translated by ±1 cell along the strip axis,
 *    animate to identity with a fade. Not a FLIP (no oldRect to invert from).
 *  - Other visibility transitions (e.g. selected fullscreen toggle): no FLIP,
 *    items fade via the .fading opacity class.
 */
const useDiscreteFlip = (
  layoutKey: string,
  visibility: Map<string, boolean>,
  pageChange: PageChangeDirection | null,
) => {
  const elementsRef = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const prevRectsRef = useRef<Map<string, DOMRect>>(new Map());
  const prevVisibilityRef = useRef<Map<string, boolean>>(new Map());
  const prevLayoutKeyRef = useRef(layoutKey);
  const initialMountRef = useRef(true);

  useLayoutEffect(() => {
    const newRects = new Map<string, DOMRect>();
    elementsRef.current.forEach((el, id) => {
      if (el) newRects.set(id, el.getBoundingClientRect());
    });

    if (initialMountRef.current) {
      initialMountRef.current = false;
      prevRectsRef.current = newRects;
      prevVisibilityRef.current = new Map(visibility);
      prevLayoutKeyRef.current = layoutKey;
      return;
    }

    if (prevLayoutKeyRef.current === layoutKey) {
      // Resize / unrelated re-render — no FLIP, just refresh rects.
      prevRectsRef.current = newRects;
      prevVisibilityRef.current = new Map(visibility);
      return;
    }

    elementsRef.current.forEach((el, id) => {
      if (!el) return;
      const newRect = newRects.get(id);
      const oldRect = prevRectsRef.current.get(id);
      const newVisible = visibility.get(id) ?? false;
      const oldVisible = prevVisibilityRef.current.get(id) ?? false;

      // Slide-in for items newly mounted via a page change.
      if (pageChange && newRect && !oldRect && newVisible) {
        const distance = pageChange.axis === "x" ? newRect.width : newRect.height;
        const tx = pageChange.axis === "x" ? distance * pageChange.sign : 0;
        const ty = pageChange.axis === "y" ? distance * pageChange.sign : 0;
        el.style.transition = "none";
        el.style.transform = `translate(${tx}px, ${ty}px)`;
        el.style.opacity = "0";
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        el.offsetHeight;
        requestAnimationFrame(() => {
          el.style.transition = `transform ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1), opacity ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
          el.style.transform = "";
          el.style.opacity = "";
        });
        return;
      }

      // Other visibility transitions (selected mode toggle, etc.): no FLIP.
      if (!oldVisible || !newVisible) return;
      if (!newRect || !oldRect) return;

      const dx = oldRect.left - newRect.left;
      const dy = oldRect.top - newRect.top;
      const sx = newRect.width === 0 ? 1 : oldRect.width / newRect.width;
      const sy = newRect.height === 0 ? 1 : oldRect.height / newRect.height;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(sx - 1) < 0.005 && Math.abs(sy - 1) < 0.005) return;

      el.style.transition = "none";
      el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
      el.style.transformOrigin = "top left";
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      el.offsetHeight;
      requestAnimationFrame(() => {
        el.style.transition = `transform ${TRANSITION_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`;
        el.style.transform = "";
      });
    });

    prevRectsRef.current = newRects;
    prevVisibilityRef.current = new Map(visibility);
    prevLayoutKeyRef.current = layoutKey;
  });

  const setItemRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) elementsRef.current.set(id, el);
    else elementsRef.current.delete(id);
  }, []);

  return { setItemRef };
};

/* -------------------------------------------------------------------------- */
/*                                 Component                                  */
/* -------------------------------------------------------------------------- */

const NewTriLayoutContainerInner = (
  {
    meeting,
    sessions,
    items,
    layout,
    focusedItemId: focusedItemIdProp,
    defaultFocusedItemId,
    onFocusChange,
    fullscreenItemId,
    paging = true,
    thumbRatio = DEFAULT_THUMB_RATIO,
    thumbShortSide = DEFAULT_THUMB_SHORT,
    style,
    className,
    defaultRendererProviderOptions,
    videosVisibilityUpdated,
    renderItem,
  }: NewTriLayoutContainerProps,
  forwardedRef: ForwardedRef<NewTriLayoutContainerRef>,
) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useElementSize(containerRef);

  /* ------------------------------ Focus state ----------------------------- */

  const [focusedItemIdInternal, setFocusedItemIdInternal] = useState<string | undefined>(defaultFocusedItemId);
  /**
   * Explicit focus = either the controlled prop or the internal state set by a
   * focus button click. Used in the layoutKey so FLIP only fires on real user
   * actions. The resolved `focusedItemId` (with fallback to items[0]) is for
   * computeLayout only — when the focused item disappears, items[0] is picked
   * silently, no transition.
   */
  const explicitFocusedItemId = focusedItemIdProp ?? focusedItemIdInternal;
  // Fall back to items[0] when the explicit id is missing OR when the item it
  // points to is no longer in the list (player removed, timeline seek through
  // a gap in VOD sources, etc.). Without this fallback, no item satisfies
  // `id === focusedItemId` and the renderer treats every player as
  // un-focused — which surfaces the focus action button on the main player.
  const focusedItemId = (
    explicitFocusedItemId && items.some((i) => i.getId() === explicitFocusedItemId)
      ? explicitFocusedItemId
      : items[0]?.getId()
  );
  const setFocusedItemId = useCallback(
    (id: string) => {
      if (focusedItemIdProp === undefined) setFocusedItemIdInternal(id);
      onFocusChange?.(id);
    },
    [focusedItemIdProp, onFocusChange],
  );

  /* -------------------------- Pagination state --------------------------- */

  const [stripPage, setStripPage] = useState(0);
  const [gridPage, setGridPage] = useState(0);

  /**
   * The page state can change for two reasons: user click, or auto-clamp when
   * items shrink. We want FLIP only on the click. So instead of putting
   * stripPage/gridPage directly in the layoutKey, we put a token that only
   * bumps on user-initiated page changes.
   */
  const [pageActionToken, bumpPageAction] = useReducer((x: number) => x + 1, 0);

  const handleStripPage = useCallback((page: number) => {
    setStripPage(page);
    bumpPageAction();
  }, []);
  const handleGridPage = useCallback((page: number) => {
    setGridPage(page);
    bumpPageAction();
  }, []);

  useEffect(() => {
    setStripPage(0);
  }, [layout, focusedItemId]);

  /* ----------------------- Thumbnail size derivation ---------------------- */

  const { thumbWidth, thumbHeight } = useMemo(() => {
    if (isHorizontalStripLayout(layout)) {
      const h = thumbShortSide;
      const w = Math.round(h * thumbRatio);
      return { thumbWidth: w, thumbHeight: h };
    }
    if (isVerticalStripLayout(layout)) {
      const w = Math.round(thumbShortSide * thumbRatio);
      const h = thumbShortSide;
      return { thumbWidth: w, thumbHeight: h };
    }
    return { thumbWidth: thumbShortSide, thumbHeight: thumbShortSide };
  }, [layout, thumbRatio, thumbShortSide]);

  /* ---------------------------- Layout compute --------------------------- */

  const layoutComputation = useMemo(
    () => computeLayout(
      items,
      layout,
      { width: containerSize.width ?? 0, height: containerSize.height ?? 0 },
      focusedItemId,
      fullscreenItemId,
      stripPage,
      gridPage,
      thumbWidth,
      thumbHeight,
    ),
    [items, layout, containerSize.width, containerSize.height, focusedItemId, fullscreenItemId, stripPage, gridPage, thumbWidth, thumbHeight],
  );

  useEffect(() => {
    if (stripPage >= layoutComputation.totalStripPages && layoutComputation.totalStripPages > 0) {
      setStripPage(layoutComputation.totalStripPages - 1);
    }
  }, [layoutComputation.totalStripPages, stripPage]);
  useEffect(() => {
    if (gridPage >= layoutComputation.totalGridPages && layoutComputation.totalGridPages > 0) {
      setGridPage(layoutComputation.totalGridPages - 1);
    }
  }, [layoutComputation.totalGridPages, gridPage]);

  /* ---------------------------- FLIP animation --------------------------- */

  /**
   * FLIP fires when any of these change in the same render:
   *  - `layout`                  (controlled, e.g. LayoutSelector click)
   *  - `explicitFocusedItemId`   (changes ONLY on focus button click or
   *                               controlled prop change — implicit fallback
   *                               to items[0] when the focused item disappears
   *                               does NOT touch this value)
   *  - `fullscreenItemId`        (controlled, parent toggles)
   *  - `pageActionToken`         (user-initiated page click only; auto-clamp
   *                               does NOT bump it)
   *
   * Items added/removed alone touch none of these, so they don't animate.
   */
  const layoutKey = `${layout}|${explicitFocusedItemId ?? ""}|${fullscreenItemId ?? ""}|${pageActionToken}`;

  // Detect a page change in the current render so freshly-mounted items can
  // slide in from the appropriate direction. Computed inline so the value is
  // available in the same render as the page state change.
  const prevStripPageRef = useRef(stripPage);
  const prevGridPageRef = useRef(gridPage);
  let pageChangeDirection: PageChangeDirection | null = null;
  if (stripPage !== prevStripPageRef.current) {
    pageChangeDirection = {
      axis: isVerticalStripLayout(layout) ? "y" : "x",
      sign: stripPage > prevStripPageRef.current ? 1 : -1,
    };
  } else if (gridPage !== prevGridPageRef.current) {
    pageChangeDirection = {
      axis: "x",
      sign: gridPage > prevGridPageRef.current ? 1 : -1,
    };
  }

  const { setItemRef } = useDiscreteFlip(layoutKey, layoutComputation.visibility, pageChangeDirection);

  // Commit the page state for the next render's diff. Runs after the FLIP
  // useLayoutEffect (declared before this in the hook), so the FLIP has
  // already consumed the direction by the time we update the refs.
  useLayoutEffect(() => {
    prevStripPageRef.current = stripPage;
    prevGridPageRef.current = gridPage;
  });

  /* ------------------------- Visibility callback ------------------------- */

  const videosVisibilityUpdatedRef = useRefAutoUpdate(videosVisibilityUpdated);
  const visibilitySnapshotRef = useRef<Map<string, boolean>>(new Map());

  useEffect(() => {
    const next = layoutComputation.visibility;
    let changed = false;
    if (next.size !== visibilitySnapshotRef.current.size) changed = true;
    if (!changed) {
      next.forEach((v, k) => {
        if (visibilitySnapshotRef.current.get(k) !== v) changed = true;
      });
    }
    if (!changed) return;
    visibilitySnapshotRef.current = new Map(next);
    const out: NewTriLayoutContainerItemVisibility[] = items.map((item) => ({
      item,
      visible: next.get(item.getId()) ?? false,
    }));
    videosVisibilityUpdatedRef.current?.(out);
  }, [layoutComputation, items]);

  useEffect(() => {
    return () => {
      videosVisibilityUpdatedRef.current?.(items.map((item) => ({ item, visible: false })));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* --------------------------- Imperative API --------------------------- */

  useImperativeHandle(
    forwardedRef,
    () => ({
      getVisibility: () => items.map((item) => ({
        item,
        visible: layoutComputation.visibility.get(item.getId()) ?? false,
      })),
    }),
    [items, layoutComputation.visibility],
  );

  /* ------------------------------ Renderer ------------------------------ */

  const rendererProviderOptions: PlayerRendererProviderOptions = useMemo(
    () => defaultRendererProviderOptions ?? { muted: true, hideSoundEffect: false },
    [defaultRendererProviderOptions],
  );

  /* ------------------------------- Render ------------------------------- */

  const renderPageNav = () => {
    if (!paging) return null;
    if (fullscreenItemId) return null;
    const isStrip = isHorizontalStripLayout(layout) || isVerticalStripLayout(layout);
    const total = isStrip ? layoutComputation.totalStripPages : layoutComputation.totalGridPages;
    if (total <= 1) return null;
    const current = isStrip ? stripPage : gridPage;
    const setPage = isStrip ? handleStripPage : handleGridPage;
    const navOrientation: "horizontal" | "vertical" = isVerticalStripLayout(layout) ? "vertical" : "horizontal";

    const PrevIcon = navOrientation === "horizontal" ? ReactIcons.ArrowLeft : ReactIcons.ArrowUp;
    const NextIcon = navOrientation === "horizontal" ? ReactIcons.ArrowRight : ReactIcons.ArrowDown;

    const prevDisabled = current <= 0;
    const nextDisabled = current >= total - 1;

    return (
      <div className={clsx(styles.pageNav, styles[navOrientation])}>
        <PrimaryButton
          onClick={prevDisabled ? undefined : () => setPage(Math.max(0, current - 1))}
          className={clsx(styles.pageNavButton, prevDisabled && styles.pageNavButtonDisabled)}
          nativeProps={{ "aria-label": "Page précédente", disabled: prevDisabled }}
        >
          <CircleIcon icon={<PrevIcon />} className={styles.pageNavCircle} />
        </PrimaryButton>
        <CarouselDots
          count={total}
          activeIndex={current}
          onSelect={setPage}
          orientation={navOrientation}
        />
        <PrimaryButton
          onClick={nextDisabled ? undefined : () => setPage(Math.min(total - 1, current + 1))}
          className={clsx(styles.pageNavButton, nextDisabled && styles.pageNavButtonDisabled)}
          nativeProps={{ "aria-label": "Page suivante", disabled: nextDisabled }}
        >
          <CircleIcon icon={<NextIcon />} className={styles.pageNavCircle} />
        </PrimaryButton>
      </div>
    );
  };

  // Resolve which item is *actually* shown fullscreen, applying the same
  // items[0] fallback as computeLayout. This keeps the per-item `isFullscreen`
  // flag honest when the explicit target has disappeared.
  const resolvedFullscreenItemId = fullscreenItemId
    ? (items.find((i) => i.getId() === fullscreenItemId)?.getId() ?? items[0]?.getId())
    : undefined;
  const isFullscreen = !!resolvedFullscreenItemId;

  const renderItems = () => {
    return items.flatMap((item) => {
      const id = item.getId();
      const placement = layoutComputation.placements.get(id);
      if (!placement) return [];
      const isFocused = id === focusedItemId;
      const isFullscreenItem = isFullscreen && id === resolvedFullscreenItemId;

      const itemStyle: React.CSSProperties = {
        gridArea: placement.gridArea,
        zIndex: placement.zIndex,
      };

      const rendererProvider = getDefaultPlayerGridItemRendererProvider(
        meeting,
        sessions,
        item,
        false,
        rendererProviderOptions,
      );

      const onFocus = () => setFocusedItemId(id);

      const ctx: NewTriLayoutContainerRenderItemContext = {
        item,
        isFocused,
        isInStrip: !isFocused && !isFullscreenItem && layout !== LayoutType.Full,
        isFullscreen: isFullscreenItem,
        onFocus,
      };

      return (
        <div
          key={id}
          ref={(el) => setItemRef(id, el)}
          className={clsx(
            styles.itemFrame,
            isFullscreenItem && styles.itemFrameFullscreen,
            placement.hidden && styles.hidden,
            placement.hidden && styles.fading,
          )}
          style={itemStyle}
        >
          {renderItem ? renderItem(rendererProvider, ctx) : rendererProvider()}
        </div>
      );
    });
  };

  const containerStyle: React.CSSProperties = layoutComputation.containerStyle ?? {};

  // The outer wrapper is a flex container; the dots sit in their own slot
  // adjacent to the strip. Direction + order are derived from the layout so
  // the dots always end up next to the strip they paginate.
  const outerFlexDirection: React.CSSProperties["flexDirection"] = isVerticalStripLayout(layout) ? "row" : "column";
  const dotsBeforeGrid = layout === LayoutType.Top || layout === LayoutType.Left;

  const outerStyle: React.CSSProperties = {
    flexDirection: outerFlexDirection,
    ...(style ?? {}),
  };

  const pageNav = renderPageNav();

  return (
    <div className={clsx(styles.outerWrapper, className)} style={outerStyle}>
      {dotsBeforeGrid && pageNav}
      <div ref={containerRef} className={styles.container} style={containerStyle}>
        {renderItems()}
      </div>
      {!dotsBeforeGrid && pageNav}
    </div>
  );
};

export const NewTriLayoutContainer = forwardRef(NewTriLayoutContainerInner) as (
  props: NewTriLayoutContainerProps & { ref?: React.ForwardedRef<NewTriLayoutContainerRef> },
) => ReturnType<typeof NewTriLayoutContainerInner>;
